import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { maintenanceAPI, sensorAPI } from '../utils/api';
import { MaintenanceRecord, SensorData } from '../types';
import { ArrowLeft, Download, Calendar, TrendingUp, DollarSign, Filter } from 'lucide-react';

const MaintenanceReports: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [generators, setGenerators] = useState<SensorData[]>([]);
  const [filters, setFilters] = useState({
    generator_id: null as number | null,
    from_date: '',
    to_date: '',
    service_status: '',
  });

  useEffect(() => {
    loadGenerators();
    loadRecords();
  }, [filters]);

  const loadGenerators = async () => {
    try {
      const response = await sensorAPI.getAllSensors();
      const generatorSensors = response.sensors.filter(s => s.type === 'engine_hours');
      setGenerators(generatorSensors);
    } catch (error) {
      console.error('Error loading generators:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filters.generator_id) params.generator_id = filters.generator_id;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.service_status) params.service_status = filters.service_status;
      
      const response = await maintenanceAPI.getRecords(params);
      setRecords(response.records.data || []);
    } catch (error: any) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: records.length,
    totalCost: records.reduce((sum, r) => {
      const cost = typeof r.cost === 'number' ? r.cost : (r.cost ? parseFloat(String(r.cost)) || 0 : 0);
      return sum + cost;
    }, 0),
    completed: records.filter(r => r.service_status === 'completed').length,
    partial: records.filter(r => r.service_status === 'partial').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/maintenance')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Maintenance Reports</h1>
                <p className="text-sm text-gray-600 mt-1">View maintenance history and analytics</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <p className="text-sm text-gray-600">Total Services</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <p className="text-sm text-gray-600">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <p className="text-sm text-gray-600">Total Cost</p>
            <p className="text-2xl font-bold text-blue-600">
              ${(typeof stats.totalCost === 'number' ? stats.totalCost : parseFloat(String(stats.totalCost)) || 0).toFixed(2)}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <p className="text-sm text-gray-600">Partial</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.partial}</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Generator</label>
              <select
                value={filters.generator_id || ''}
                onChange={(e) => setFilters({ ...filters, generator_id: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Generators</option>
                {generators.map((gen) => (
                  <option key={gen.id} value={gen.id}>
                    {gen.name} {gen.location ? `- ${gen.location}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.service_status}
                onChange={(e) => setFilters({ ...filters, service_status: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="partial">Partial</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
          </div>
        </div>

        {/* Records Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading records...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No maintenance records</h3>
            <p className="text-gray-600">No records match your filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Generator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.service_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.generator ? (
                        <>
                          {record.generator.name}
                          {record.generator.device_name && (
                            <span className="text-gray-600 ml-1">({record.generator.device_name})</span>
                          )}
                          {record.generator.location && (
                            <span className="text-gray-500 ml-1">- {record.generator.location}</span>
                          )}
                        </>
                      ) : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.template?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.service_run_hours 
                        ? `${(typeof record.service_run_hours === 'number' ? record.service_run_hours : parseFloat(String(record.service_run_hours)) || 0).toFixed(1)}h`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.cost 
                        ? `$${(typeof record.cost === 'number' ? record.cost : parseFloat(String(record.cost)) || 0).toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        record.service_status === 'completed' ? 'bg-green-100 text-green-800' :
                        record.service_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {record.service_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceReports;

